import { Fragment, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { X } from 'lucide-react'
import { toast } from 'react-toastify'
import { phoneNumbersAPI, appsAPI } from '../services/api'
import LoadingSpinner from './LoadingSpinner'

function PhoneNumberModal({ isOpen, onClose, phoneNumber }) {
  const queryClient = useQueryClient()
  const isEditing = !!phoneNumber

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm()

  const { data: carriers, isLoading: carriersLoading } = useQuery(
    'carriers',
    phoneNumbersAPI.getCarriers,
    { enabled: isOpen && !isEditing }
  )

  const { data: apps, isLoading: appsLoading } = useQuery(
    'apps',
    appsAPI.getApps,
    { enabled: isOpen }
  )

  const createMutation = useMutation(phoneNumbersAPI.createPhoneNumber, {
    onSuccess: () => {
      toast.success('Phone number added successfully')
      queryClient.invalidateQueries('phone-numbers')
      onClose()
      reset()
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to add phone number')
    }
  })

  const updateMutation = useMutation(
    ({ id, data }) => phoneNumbersAPI.updatePhoneNumber(id, data),
    {
      onSuccess: () => {
        toast.success('Phone number updated successfully')
        queryClient.invalidateQueries('phone-numbers')
        onClose()
        reset()
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update phone number')
      }
    }
  )

  useEffect(() => {
    if (phoneNumber) {
      reset({
        phoneNumber: phoneNumber.phoneNumber,
        carrierId: phoneNumber.carrierId,
        applicationId: phoneNumber.applicationId || ''
      })
    } else {
      reset({
        phoneNumber: '',
        carrierId: '',
        applicationId: ''
      })
    }
  }, [phoneNumber, reset])

  const onSubmit = (data) => {
    if (isEditing) {
      updateMutation.mutate({ id: phoneNumber._id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const isLoading = createMutation.isLoading || updateMutation.isLoading

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-primary-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-primary-400 hover:text-primary-500"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-primary-900">
                      {isEditing ? 'Edit Phone Number' : 'Add Phone Number'}
                    </Dialog.Title>

                    <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
                      {!isEditing && (
                        <div>
                          <label className="block text-sm font-medium text-primary-700 mb-1">
                            Phone Number *
                          </label>
                          <input
                            {...register('phoneNumber', { required: 'Phone number is required' })}
                            type="text"
                            className="input-field"
                            placeholder="+1234567890"
                          />
                          {errors.phoneNumber && (
                            <p className="mt-1 text-sm text-red-600">{errors.phoneNumber.message}</p>
                          )}
                        </div>
                      )}

                      {!isEditing && (
                        <div>
                          <label className="block text-sm font-medium text-primary-700 mb-1">
                            Carrier *
                          </label>
                          {carriersLoading ? (
                            <div className="flex items-center justify-center py-2">
                              <LoadingSpinner size="sm" />
                            </div>
                          ) : (
                            <select
                              {...register('carrierId', { required: 'Carrier is required' })}
                              className="input-field"
                            >
                              <option value="">Select a carrier</option>
                              {carriers?.data?.carriers?.map((carrier) => (
                                <option key={carrier.voip_carrier_sid} value={carrier.voip_carrier_sid}>
                                  {carrier.name}
                                </option>
                              ))}
                            </select>
                          )}
                          {errors.carrierId && (
                            <p className="mt-1 text-sm text-red-600">{errors.carrierId.message}</p>
                          )}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-primary-700 mb-1">
                          Application
                        </label>
                        {appsLoading ? (
                          <div className="flex items-center justify-center py-2">
                            <LoadingSpinner size="sm" />
                          </div>
                        ) : (
                          <select
                            {...register('applicationId')}
                            className="input-field"
                          >
                            <option value="">No application assigned</option>
                            {apps?.data?.apps?.map((app) => (
                              <option key={app.jambonzId} value={app.jambonzId}>
                                {app.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={onClose}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="btn-primary"
                        >
                          {isLoading && <LoadingSpinner size="sm" className="mr-2" />}
                          {isLoading ? 'Saving...' : isEditing ? 'Update' : 'Add'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default PhoneNumberModal